import { getIdToken } from './firebase';

// Auto-detect API URL based on current domain
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // If on remotetime.work, use api.remotetime.work
  if (typeof window !== 'undefined' && window.location.hostname === 'remotetime.work') {
    return 'https://api.remotetime.work';
  }
  
  // Otherwise use the worker URL
  return 'https://clocksync-api-production.beta127.workers.dev';
};

const API_URL = getApiUrl();

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  requireAuth?: boolean;
}

/**
 * Make an API request
 */
async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, requireAuth = true } = options;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (requireAuth) {
    const token = await getIdToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  
  return response.json();
}

// API functions

/**
 * Verify auth and get/create user profile
 */
export async function verifyAuth() {
  return apiRequest<{ user: any }>('/auth/verify', { method: 'POST' });
}

/**
 * Get current user profile
 */
export async function getMyProfile() {
  return apiRequest<{ user: any }>('/users/me');
}

/**
 * Update user profile
 */
export async function updateMyProfile(data: {
  displayName?: string;
  avatarUrl?: string;
  timezone?: string;
  location?: string;
  workHourStart?: number;
  workHourEnd?: number;
}) {
  return apiRequest<{ user: any }>('/users/me', { method: 'PUT', body: data });
}

/**
 * Get all people I can see (shared + manual)
 */
export async function getPeople() {
  return apiRequest<{ people: any[] }>('/shares');
}

/**
 * Create an invitation link
 */
export async function createInvitation(options?: { maxUses?: number; expiresIn?: number }) {
  return apiRequest<{ invitation: { code: string; url: string } }>('/shares/invite', {
    method: 'POST',
    body: options,
  });
}

/**
 * Accept an invitation
 */
export async function acceptInvitation(code: string) {
  return apiRequest<{ share: any }>('/shares/accept', {
    method: 'POST',
    body: { code },
  });
}

/**
 * Remove a share
 */
export async function removeShare(shareId: string) {
  return apiRequest<{ success: boolean }>(`/shares/${shareId}`, { method: 'DELETE' });
}

/**
 * Add a manual person (cloud sync)
 */
export async function addManualPerson(person: {
  name: string;
  avatar: string;
  timezone: string;
  location: string;
  workHourStart: number;
  workHourEnd: number;
}) {
  return apiRequest<{ person: any }>('/people', { method: 'POST', body: person });
}

/**
 * Update a manual person
 */
export async function updateManualPerson(id: string, data: Partial<{
  name: string;
  avatar: string;
  timezone: string;
  location: string;
  workHourStart: number;
  workHourEnd: number;
}>) {
  return apiRequest<{ person: any }>(`/people/${id}`, { method: 'PUT', body: data });
}

/**
 * Delete a manual person
 */
export async function deleteManualPerson(id: string) {
  return apiRequest<{ success: boolean }>(`/people/${id}`, { method: 'DELETE' });
}
